<template>
    <a
        ref="bannerRef"
        class="product-list__banner"
        :class="[{ 'product-list__banner--image-only': banner.title.length <= 1 }, resolveColorVariantCssVariant()]"
        :href="banner.url"
        :data-id="banner.id"
        :data-title="banner.title"
        :style="`background-color: ${banner.backgroundColor}`"
    >
        <div v-if="banner.title.length > 1">
            <div v-if="banner.title.length > 1" class="product-list__banner__title">
                {{ banner.title }}
            </div>
            <div v-if="banner.description.length > 1" class="product-list__banner__description">
                {{ banner.description }}
            </div>
        </div>
        <img :src="banner.cdnImage" alt="Banner" loading="lazy" />
        <div v-if="banner.title.length > 1">
            <div v-if="banner.cta.length > 1" class="product-list__banner__cta">
                {{ banner.cta }}
            </div>
            <div v-if="banner.warningMessage.length > 1" class="product-list__banner__warning-text">
                {{ banner.warningMessage }}
            </div>
        </div>
    </a>
</template>

<script lang="ts" setup>
import { useMatomoViewportTrack, useMatomoClickTrack } from '~/composables/useMatomo'
import ProductListBanner from '~/models/components/ProductListBanner'

const props = defineProps<{
    banner: ProductListBanner
}>()

const bannerRef = ref<HTMLElement | null>(null)

const resolveColorVariantCssVariant = () => {
    if (props.banner.colorVariant === 'white') {
        return 'product-list__banner--white'
    }
    if (props.banner.colorVariant === 'brand') {
        return 'product-list__banner--brand'
    }

    return 'product-list__banner--dark'
}

onMounted(() => {
    if (!bannerRef.value) {
        return
    }

    useMatomoViewportTrack(bannerRef.value, {
        category: 'Catalog Banners',
        action: 'view',
        name: bannerRef.value.dataset.title,
        value: +(bannerRef.value.dataset.id ?? 0),
    })

    useMatomoClickTrack(bannerRef.value, {
        category: 'Catalog Banners',
        action: 'click',
        name: bannerRef.value.dataset.title,
        value: +(bannerRef.value.dataset.id ?? 0),
    })
})
</script>

<style lang="scss" scoped>
$gap-mobile: 8px;
$gap: 36px;
.product-list__banner {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: space-between;
    border-bottom: 1px solid $color-grey-dark;
    width: 100%;
    min-height: calc(100% - 25px);
    text-align: center;
    padding: 15px 0;
    box-shadow: 0 1px 6px rgb(148 170 179 / 30%);
    border-radius: 10px;

    &:only-child {
        justify-content: space-evenly;
    }

    &--image-only {
        padding: 0;
    }

    @include screen-sm {
        border-bottom: none;
    }

    img {
        width: 100%;
    }

    &__title {
        font-family: 'scale-variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans',
            'Droid Sans', 'Helvetica Neue', sans-serif;
        font-variation-settings: 'wght' 700, 'wdth' 110;
        font-size: 21px;
        margin-bottom: 5px;
        padding: 0 10px;
    }

    &__description {
        font-family: 'scale-variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans',
            'Droid Sans', 'Helvetica Neue', sans-serif;
        font-variation-settings: 'wght' 400, 'wdth' 110;
        font-size: 18px;
        line-height: 21px;
        padding: 0 10px;
    }

    &__cta {
        background: $color-primary-green;
        color: $color-secondary-accent-green;
        max-width: 170px;
        margin: 0 auto 5px;
        border-radius: 15px;
        line-height: 30px;
    }

    &__warning-text {
        color: $color-grey-extra-dark;
        font-size: 11px;
        padding: 0 10px;
    }

    &--white {
        .product-list__banner {
            &__title {
                color: $color-white;
            }

            &__description {
                color: $color-white;
            }

            &__cta {
                background: $color-white;
                color: $color-secondary-dark-green-1;
            }
        }
    }

    &--dark {
        .product-list__banner {
            &__title {
                color: #004840;
            }

            &__description {
                color: #004840;
            }

            //&__cta {
            //}
        }
    }

    &--brand {
        .product-list__banner {
            &__title {
                color: $color-primary-pink;
            }

            &__description {
                color: $color-primary-pink;
            }

            &__cta {
                background: $color-primary-pink;
                color: $color-primary-green;
            }
        }
    }

    &:hover {
        .product-list__banner__cta {
            background: $color-secondary-accent-green;
            color: $color-primary-green;
        }
    }
}
</style>
